import React, { useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { SERVER_BASE_URL } from "../Config";

interface UploadStats {
  total_rows: number;
  products_created: number;
  products_updated: number;
  products_hidden: number;
  errors: string[];
}

interface UploadResponse {
  success: boolean;
  message: string;
  stats?: UploadStats;
}

const AdminUpload: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setUploadResult({
          success: false,
          message: "Please select a valid Excel file (.xlsx or .xls)"
        });
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(`${SERVER_BASE_URL}/admin/upload-products`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${pharmacistToken}`,
        },
        body: formData,
      });

      const data: UploadResponse = await response.json();
      setUploadResult(data);
    } catch (error) {
      setUploadResult({
        success: false,
        message: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setUploadResult(null);
  };

  return (
    <Paper sx={{ p: 3, flex: 1 }}>
      <Typography variant="h5" mb={2}>
        Product Database Administration
      </Typography>
      
      <Typography variant="body2" color="text.secondary" mb={3}>
        Upload an Excel file to update the product database. This will safely upsert products by marking old ones as hidden and adding/updating products from the spreadsheet.
      </Typography>

      <Box mb={3}>
        <input
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          id="excel-file-upload"
          type="file"
          onChange={handleFileSelect}
        />
        <label htmlFor="excel-file-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<CloudUploadIcon />}
            fullWidth
            sx={{ mb: 2 }}
          >
            Select Excel File
          </Button>
        </label>

        {selectedFile && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              Selected file: <strong>{selectedFile.name}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Size: {(selectedFile.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            fullWidth
          >
            {uploading ? 'Uploading...' : 'Upload and Process'}
          </Button>
          
          {(selectedFile || uploadResult) && (
            <Button
              variant="outlined"
              onClick={resetUpload}
              disabled={uploading}
            >
              Reset
            </Button>
          )}
        </Box>
      </Box>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            Processing Excel file and updating database...
          </Typography>
        </Box>
      )}

      {uploadResult && (
        <Alert 
          severity={uploadResult.success ? "success" : "error"} 
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-line' }}>
            {uploadResult.message}
          </Typography>
        </Alert>
      )}

      {uploadResult?.success && uploadResult.stats && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" mb={2}>Upload Statistics</Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Total Rows Processed" 
                secondary={uploadResult.stats.total_rows} 
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText 
                primary="Products Created" 
                secondary={uploadResult.stats.products_created} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Products Updated" 
                secondary={uploadResult.stats.products_updated} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Products Hidden" 
                secondary={uploadResult.stats.products_hidden} 
              />
            </ListItem>
            {uploadResult.stats.errors.length > 0 && (
              <>
                <Divider />
                <ListItem>
                  <ListItemText 
                    primary="Errors" 
                    secondary={uploadResult.stats.errors.length} 
                  />
                </ListItem>
              </>
            )}
          </List>
          
          {uploadResult.stats.errors.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="error">Error Details:</Typography>
              {uploadResult.stats.errors.map((error, index) => (
                <Typography key={index} variant="caption" color="error" display="block">
                  {error}
                </Typography>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </Paper>
  );
};

export default AdminUpload;